import { useApi } from 'src/hooks/useApi';
import { urlJoin } from 'src/utils/util';


const resourceName = 'folder';

const useProjectApi = () => {
  const { get, post, del, patch } = useApi();
  return {
    getProjects: async params => (await get(resourceName, params)).data,
    getLatestProjects: async () => (await get(urlJoin(resourceName, 'by-last-event'))).data,
    getProject: async projectId => (await get(resourceName, { folderId: projectId })).data,
    createProject: async newProject => (await post(resourceName, newProject)).data,
    deleteProject: async projectId => (await del(urlJoin(resourceName, projectId))).data,
    updateProject: async ({ projectId, ...params }) => (await patch(urlJoin(resourceName, projectId), params)).data,

    getLinkedAuditableUnits: async projectId => (await get(urlJoin(resourceName, projectId, 'title-holder-and-units'))).data,
    linkAuditableUnit: async ({ projectId, auditableUnitId, titleHolderId }) =>
      (await post(urlJoin(resourceName, projectId, 'link-by-title-holder'), { auditableUnitId, titleHolderId })).data,
    unlinkAuditableUnit: async ({ projectId, auditableUnitId, titleHolderId }) =>
      (await del(urlJoin(resourceName, projectId, 'unlink-by-title-holder'), { auditableUnitId, titleHolderId })).data,
    getIssues: async projectId => (await get(urlJoin(resourceName, projectId, 'warning-data'))).data,
    rcaSearch: async ({ projectId, ...params }) => (await get(urlJoin(resourceName, projectId, 'search/rca'), params)).data,
    monitoringPlanSearch: async ({ projectId, ...params }) =>
      (await get(urlJoin(resourceName, projectId, 'search/monitoring-plan'), params)).data,
    getUnassignedCampaigns: async projectId => (await get(urlJoin(resourceName, projectId, 'unassigned-campaigns'))).data,

    getGeneralInfo: async ({ projectId, ...params }) =>
      (await get(urlJoin(resourceName, projectId, 'info'), { ...params })).data,
    getSpeciesInfo: async ({ projectId, ...params }) =>
      (await get(urlJoin(resourceName, projectId, 'species-info'), { ...params })).data,
    getSpeciesAllocationInfo: async ({ projectId, ...params }) =>
      (await get(urlJoin(resourceName, projectId, 'species-allocation-info'), { ...params })).data,
    getEeccOccurrences: async ({ projectId, ...params }) =>
      (await get(urlJoin(resourceName, projectId, 'eecc-occurrences'), { ...params })).data,

    getLinkedCaptureCompanies: async projectId => (await get(urlJoin(resourceName, projectId, 'linked-capture-companies'))).data,
    getLinkedCampaigns: async projectId => (await get(urlJoin(resourceName, projectId, 'linked-capture-campaigns'))).data,
    importCampaigns: async ({ projectId, companyId, campaignIds }) => (await post(
      urlJoin(resourceName, projectId, 'link-capture-campaign'), { captureCampaignIds: campaignIds, captureCompanyId: companyId },
    )).data,
    removeImportedCampaign: async ({ projectId, campaignId }) =>
      (await post(urlJoin(resourceName, projectId, 'unlink-capture-campaign'), { captureCampaignId: campaignId })).data,
    getLinkedProjects: async projectId => (await get(urlJoin(resourceName, projectId, 'linked-capture-projects'))).data,
    importProjects: async ({ projectId, companyId, projectIds }) => (await post(
      urlJoin(resourceName, projectId, 'link-capture-project'), { captureProjectIds: projectIds, captureCompanyId: companyId },
    )).data,
    removeImportedProject: async ({ projectId, captureProjectId }) =>
      (await post(urlJoin(resourceName, projectId, 'unlink-capture-project'), { captureProjectId })).data,

    getInterestArea: async ({ projectId }) => (await get(urlJoin(resourceName, projectId, 'area-of-interest'))).data,
    getSitesInterestArea: async ({ projectId }) => (await get(urlJoin(resourceName, projectId, 'sites-interest-area'))).data,
    importInterestArea: async ({ projectId, interestArea }) =>
      (await post(urlJoin(resourceName, projectId, 'area-of-interest'), interestArea, { isMultipart: true })).data,
    deleteInterestArea: async ({ projectId, areaOfInterestKmlId }) =>
      (await del(urlJoin(resourceName, projectId, 'area-of-interest'), { areaOfInterestKmlId })).data,
    getGeospatialAnalytics: async ({ projectId, ...params }) =>
      (await get(urlJoin(resourceName, projectId, 'geospatial-analytics'), params)).data,

    processInterestArea: async({ projectId }) => (await post(urlJoin(resourceName, projectId, 'prepare-terrain-data'))).data,
    updateGeospatialConfig: async ({ projectId, config }) => (await patch(urlJoin(resourceName, projectId, 'geospatial-config'),
      { config })).data,

    processLandcoverStats: async projectId => (await post(urlJoin(resourceName, projectId, 'landcover-stats'))).data,

    getPrefeasibilityLayer: async ({ projectId, layerId }) =>
      (await get(urlJoin('prefeasibility', layerId), { projectId })).data,
    processPrefeasibilityLayer: async ({ projectId, layerId }) =>
      (await post(urlJoin('prefeasibility', layerId), { projectId })).data,
    getProtectedAndPriorityZones: async projectId =>
      (await get(urlJoin('prefeasibility', 'protected-and-priority-zones'), { projectId })).data,
    processProtectedAndPriorityZones: async projectId =>
      (await post(urlJoin('prefeasibility', 'protected-and-priority-zones'), { projectId })).data,
    getInProgressAnalytics: async projectId =>
      (await get(urlJoin(resourceName, projectId, 'analytics-in-progress'))).data,

    getPressuresAndDependencies: async projectId =>
      (await get(urlJoin(resourceName, projectId, 'pressures-and-dependencies'))).data,
    getIsic: async projectId =>
      (await get(urlJoin(resourceName, projectId, 'isic'))).data,

    getEcosystems: async ({ projectId, ...params }) =>
      (await get(urlJoin(resourceName, projectId, 'ecosystems'), params)).data,
    getSites: async projectId =>
      (await get(urlJoin(resourceName, projectId, 'sites'))).data,
    searchSites: async ({ projectId, ...params }) =>
      (await get(urlJoin(resourceName, projectId, 'search/sites'), params)).data,

    getSpatialFootprint: async projectId =>
      (await get(urlJoin(resourceName, projectId, 'spatial-footprint'))).data,
    getGeospatialOptions: async ({ projectId, ...params }) =>
      (await get(urlJoin(resourceName, projectId, `geospatial-options`), params)).data,
    getProjectOtherStats: async ({ projectId }) =>
      (await get(urlJoin(resourceName, projectId, 'project-other-stats'))).data,
    getNatureStateIndicators: async ({ projectId }) =>
      (await get(urlJoin(resourceName, projectId, 'nature-state-indicators'))).data,

    getSitesInfo: async companyId => (await get(urlJoin(resourceName, 'companies', companyId, 'sites-info'))).data,

    getProjectProgressStatus: async projectId =>
      (await get(urlJoin(resourceName, projectId, 'progress-status'))).data,
    getThumbnail: async ({ projectId, fileName }) =>
      (await get(urlJoin(resourceName, projectId, 'thumbnail', fileName))).data,
  };
};


export { useProjectApi };
